import {
  NoTaxNumberReasonCode,
  TaxInfoEntry,
  UsTaxNoReturnReasonCode,
} from '@/types/api/tax-info';
import {
  DocumentStatus,
  DocumentType,
  RequiredCondition,
  RequiredDocument,
  RequiredDocumentGroup,
} from '@/types/document';
import { TranslatorFunction } from '@/types/i18n';
import { CountryISO } from '@/types/places';

import { getHigherStatus } from '@/utils/document';

export const TAX_SUPPORTING_DOCUMENT_YOUR_COUNTRY_KEY =
  'documentSection.Tax_Supporting_Document.yourCountryTaxDocument';

export const TAX_SUPPORTING_DOCUMENT_COUNTRY_GROUP_TITLE_KEY =
  'documentSection.Tax_Supporting_Document.countryDocuments';

export type TaxSupportingDocumentUploadGuidance = {
  guidanceKey: string;
  guidanceParams: Record<string, string>;
};

export const TAX_SUPPORTING_DOCUMENT_MATCH_CONFIG: Partial<
  Record<DocumentType, (entry: TaxInfoEntry) => boolean>
> = {
  [DocumentType.CertificateOfLossOfNationality]: entry =>
    entry.usTaxNoReturnReasonCode ===
    UsTaxNoReturnReasonCode.RENOUNCED_CITIZENSHIP,
  [DocumentType.FormI407Proof]: entry =>
    entry.usTaxNoReturnReasonCode ===
    UsTaxNoReturnReasonCode.RENOUNCED_GREEN_CARD,
  [DocumentType.ProofOfCurrentAddress]: entry =>
    entry.usTaxNoReturnReasonCode ===
    UsTaxNoReturnReasonCode.US_ADDRESS_TEMPORARY_ONLY,
  [DocumentType.TaxExemptionDocument]: entry =>
    entry.usTaxNoReturnReasonCode ===
    UsTaxNoReturnReasonCode.EXEMPT_UNDER_TAX_TREATY,
  [DocumentType.ResidencyVisaEntryPermitProof]: entry =>
    entry.noTaxNumberReasonCode ===
    NoTaxNumberReasonCode.NEW_RESIDENT_NO_TIN_YET,
  [DocumentType.DiplomaticIdOrTaxExemptionLetter]: entry =>
    entry.noTaxNumberReasonCode === NoTaxNumberReasonCode.LEGAL_EXEMPTION,
};

export function getTaxSupportingDocumentCountryLabel(
  countryCode: string | undefined,
  translatedCountry: Record<string, string>,
): string {
  if (!countryCode) {
    return '';
  }

  if (countryCode === CountryISO.US) {
    return 'US';
  }

  return translatedCountry[countryCode] ?? countryCode;
}

function getTaxSupportingDocumentCountryCode(
  docType: DocumentType,
  taxInfo?: TaxInfoEntry[],
): string | undefined {
  const match = TAX_SUPPORTING_DOCUMENT_MATCH_CONFIG[docType];
  if (!match || !taxInfo?.length) {
    return undefined;
  }

  return taxInfo.find(match)?.countryCode;
}

function getTaxSupportingCountryGroupSubtitle(
  status: DocumentStatus | undefined,
): string {
  switch (status) {
    case DocumentStatus.InReview:
      return 'status.underReview';
    case DocumentStatus.Rejected:
      return 'status.rejected';
    case DocumentStatus.Approved:
      return 'status.approved';
    default:
      return 'status.required';
  }
}

export function groupTaxSupportingDocumentsByCountry(params: {
  items: RequiredDocument[];
  taxInfo?: TaxInfoEntry[];
  t: TranslatorFunction;
  translatedCountry: Record<string, string>;
}): RequiredDocumentGroup[] {
  const { items, taxInfo, t, translatedCountry } = params;
  const itemsByCountryCode = new Map<string, RequiredDocument[]>();
  const ungroupedItems: RequiredDocument[] = [];

  items.forEach(item => {
    const countryCode = getTaxSupportingDocumentCountryCode(item.name, taxInfo);
    if (!countryCode) {
      ungroupedItems.push(item);
      return;
    }

    const existing = itemsByCountryCode.get(countryCode) ?? [];
    existing.push(item);
    itemsByCountryCode.set(countryCode, existing);
  });

  const otherCountryCodes = Array.from(itemsByCountryCode.keys())
    .filter(code => code !== CountryISO.US)
    .sort((a, b) => a.localeCompare(b));

  const orderedCountryCodes = itemsByCountryCode.has(CountryISO.US)
    ? [CountryISO.US, ...otherCountryCodes]
    : otherCountryCodes;

  const countryGroups = orderedCountryCodes.map(countryCode => {
    const groupItems = itemsByCountryCode.get(countryCode) ?? [];
    const status =
      getHigherStatus(
        groupItems.map(item => item.status),
        RequiredCondition.And,
      ) || DocumentStatus.Pending;
    const countryLabel = getTaxSupportingDocumentCountryLabel(
      countryCode,
      translatedCountry,
    );

    return {
      condition: RequiredCondition.And,
      items: groupItems,
      status,
      title: t(TAX_SUPPORTING_DOCUMENT_COUNTRY_GROUP_TITLE_KEY, {
        country: countryLabel,
      }),
      subtitle: getTaxSupportingCountryGroupSubtitle(status),
    } satisfies RequiredDocumentGroup;
  });

  if (!ungroupedItems.length) {
    return countryGroups;
  }

  const ungroupedStatus =
    getHigherStatus(
      ungroupedItems.map(item => item.status),
      RequiredCondition.And,
    ) || DocumentStatus.Pending;

  return [
    ...countryGroups,
    {
      condition: RequiredCondition.And,
      items: ungroupedItems,
      status: ungroupedStatus,
      subtitle: getTaxSupportingCountryGroupSubtitle(ungroupedStatus),
    },
  ];
}

export function getTaxSupportingDocumentUploadGuidance(params: {
  docType?: DocumentType;
  taxInfo?: TaxInfoEntry[];
  translatedCountry: Record<string, string>;
}): TaxSupportingDocumentUploadGuidance | undefined {
  const { docType, taxInfo, translatedCountry } = params;
  if (!docType || !taxInfo?.length) {
    return undefined;
  }

  const countryCode = getTaxSupportingDocumentCountryCode(docType, taxInfo);
  if (!countryCode) {
    return undefined;
  }

  return {
    guidanceKey: TAX_SUPPORTING_DOCUMENT_YOUR_COUNTRY_KEY,
    guidanceParams: {
      country: getTaxSupportingDocumentCountryLabel(
        countryCode,
        translatedCountry,
      ),
    },
  };
}
